import { useState, useEffect, useCallback } from 'react';
import { X, Trash2 } from 'lucide-react';
import type { TaskTemplate } from '../../types/tasks';
import { AGENTS } from '../../utils/agents';
import { useTaskStore } from '../../stores/taskStore';

const CADENCE_OPTIONS = ['daily', 'weekly', 'biweekly', 'monthly', 'quarterly', 'trigger'] as const;

interface CreateTemplateModalProps {
  template?: TaskTemplate;
  onClose: () => void;
}

export default function CreateTemplateModal({ template, onClose }: CreateTemplateModalProps) {
  const createTemplate = useTaskStore((s) => s.createTemplate);
  const updateTemplate = useTaskStore((s) => s.updateTemplate);
  const deleteTemplate = useTaskStore((s) => s.deleteTemplate);

  const isEdit = !!template;

  const [title, setTitle] = useState(template?.title || '');
  const [description, setDescription] = useState(template?.description || '');
  const [assignedAgent, setAssignedAgent] = useState(template?.assigned_agent || '');
  const [cadence, setCadence] = useState(template?.cadence || '');
  const [skillUsed, setSkillUsed] = useState(template?.skill_used || '');
  const [instructions, setInstructions] = useState(template?.instructions || '');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleClose = useCallback(() => onClose(), [onClose]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') handleClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleClose]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const data = {
        title: title.trim(),
        description: description.trim(),
        assigned_agent: assignedAgent || null,
        cadence: cadence || null,
        skill_used: skillUsed.trim() || null,
        instructions: instructions.trim(),
      };

      if (isEdit && template) {
        await updateTemplate(template.id, data);
      } else {
        await createTemplate(data);
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save template');
      setSubmitting(false);
    }
  }

  async function handleDelete() {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    if (template) {
      try {
        await deleteTemplate(template.id);
        onClose();
      } catch (err) {
        console.error('Failed to delete template:', err);
      }
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={handleClose} />
      <div className="relative bg-bg-secondary border border-border-subtle rounded-xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-text-primary">
            {isEdit ? 'Edit Template' : 'New Template'}
          </h2>
          <button
            onClick={handleClose}
            className="p-1 rounded hover:bg-bg-tertiary text-text-muted hover:text-text-primary transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label className="block text-sm text-text-secondary mb-1">Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-2 w-full text-sm focus:outline-none focus:border-accent-purple"
              placeholder="Template title"
              autoFocus
            />
            {error && <p className="text-error text-xs mt-1">{error}</p>}
          </div>

          <div>
            <label className="block text-sm text-text-secondary mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-2 w-full text-sm focus:outline-none focus:border-accent-purple resize-none"
              placeholder="Brief description of this template"
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-text-secondary mb-1">Assigned Agent</label>
              <select
                value={assignedAgent}
                onChange={(e) => setAssignedAgent(e.target.value)}
                className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-2 w-full text-sm"
              >
                <option value="">Unassigned</option>
                {AGENTS.map((a) => (
                  <option key={a.slug} value={a.slug}>
                    {a.name} — {a.title}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm text-text-secondary mb-1">Cadence</label>
              <select
                value={cadence}
                onChange={(e) => setCadence(e.target.value)}
                className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-2 w-full text-sm"
              >
                <option value="">None</option>
                {CADENCE_OPTIONS.map((c) => (
                  <option key={c} value={c}>
                    {c.charAt(0).toUpperCase() + c.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm text-text-secondary mb-1">Skill Used</label>
            <input
              type="text"
              value={skillUsed}
              onChange={(e) => setSkillUsed(e.target.value)}
              className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-2 w-full text-sm focus:outline-none focus:border-accent-purple"
              placeholder="/skill-name"
            />
          </div>

          <div>
            <label className="block text-sm text-text-secondary mb-1">Instructions</label>
            <textarea
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-2 w-full text-sm focus:outline-none focus:border-accent-purple resize-none font-mono"
              placeholder="Task instructions (markdown supported)"
              rows={6}
            />
          </div>

          <div className="flex justify-between items-center mt-2">
            {isEdit ? (
              <button
                type="button"
                onClick={handleDelete}
                className={`flex items-center gap-1.5 text-sm transition-colors px-3 py-2 rounded-md ${
                  confirmDelete
                    ? 'bg-error/15 text-error'
                    : 'text-text-muted hover:text-error'
                }`}
              >
                <Trash2 className="h-4 w-4" />
                {confirmDelete ? 'Confirm Delete' : 'Delete'}
              </button>
            ) : (
              <div />
            )}

            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleClose}
                className="text-text-muted hover:text-text-secondary text-sm transition-colors px-3 py-2"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="bg-accent-purple text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-accent-purple/80 transition-colors disabled:opacity-50"
              >
                {submitting ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Template'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
